import {
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Card,
} from "@/components/Primitives/Card"
import { Button } from "@/components/Primitives/Button"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"
import { cn } from "@/lib/utils/utils"
import { Icon } from "@/components/Primitives/Icon"
import { Bell, X } from "lucide-react"
import { useState } from "react"
import { useConfig } from "@/hooks/useConfig"

interface SoonImageCardProps {
  title: string
  description: string
  imageSrc: string
  imageAlt: string
  className?: string
  questId: string
  onNotify: (questId: string) => Promise<void>
  isSubscribed?: boolean
}

export function SoonImageCard({
  title,
  description,
  imageSrc,
  imageAlt,
  className = "",
  questId,
  onNotify,
  isSubscribed = false,
}: SoonImageCardProps) {
  const { t } = useConfig()
  const [isLoading, setIsLoading] = useState(false)

  const handleNotifyClick = async () => {
    setIsLoading(true)
    try {
      await onNotify(questId)
    } catch (error) {
      console.error("Error notifying for quest:", error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card
      className={cn(
        "overflow-hidden bg-neutral-100 p-0 flex-row gap-0",
        className,
      )}
    >
      <div className="relative w-1/2">
        <img
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-neutral-800/30"></div>
      </div>
      <div className="w-1/2 p-4 flex flex-col justify-between">
        <div>
          <div className="w-fit z-10 bg-purple-50 text-purple-500 px-2 rounded-sm text-[0.75rem] font-semibold">
            {t("config_quest_waitlist")}
          </div>
          <CardHeader className="px-0 mt-4">
            <CardTitle className="text-[1rem]">{title}</CardTitle>
          </CardHeader>
          <CardContent className="text-[1rem] my-0 px-0 [&_p]:!my-0 [&_ul]:!my-0 [&_ol]:!my-0 [&_blockquote]:!my-0">
            <MarkdownRenderer content={description} className="!my-0" />
          </CardContent>
        </div>

        <CardFooter className="justify-end px-0 pt-4">
          <Button
            type="button"
            className="bg-purple-50 text-purple-500 font-semibold hover:bg-purple-100 text-[0.875rem]"
            onClick={handleNotifyClick}
            disabled={isLoading}
          >
            {isSubscribed ? (
              <Icon icon={X} size="md" className="text-purple-500" />
            ) : (
              <Icon icon={Bell} size="md" className="text-purple-500" />
            )}
            {isLoading
              ? t("config_button_submitting")
              : isSubscribed
              ? t("config_button_quest_unsubscribe")
              : t("config_button_quest_notify")}
          </Button>
        </CardFooter>
      </div>
    </Card>
  )
}
